/**
 * The sheet tab strip (Requirements 1.3, 1.11, 1.12).
 *
 * Add, rename, delete, activate. Four operations, three of which are commands the
 * kernel validates — which is why this component never enforces the 10-sheet limit
 * itself: it asks for a new sheet and *reports what came back*. The limit lives in
 * the dispatcher's `validateCommand`, the message comes from `LimitError`, and
 * Requirement 1.11's "display the message that the 10-Sheet limit has been reached"
 * is satisfied by showing that message rather than by a second copy of the rule
 * here. Two copies of a limit is how a limit drifts.
 *
 * Renaming is inline: double-clicking a tab (or pressing Enter on it) swaps the
 * label for a text input, Enter commits and Escape cancels. A `prompt()` would be
 * shorter and is unusable with a screen reader, untestable in a component test, and
 * blocks the main thread while a repaint is pending.
 *
 * Activation is main-thread state: the kernel holds every sheet, so switching tabs
 * is a viewport change, not a mutation, which is what makes Requirement 1.12's
 * 200 ms budget reachable.
 */

import type { SheetId } from '../core/types.js';
import type { SheetTab } from '../grid/viewTile.js';
import { button, el } from './dom.js';

export interface SheetTabsCallbacks {
  onActivate(sheet: SheetId): void;
  onAdd(): void;
  onRename(sheet: SheetId, name: string): void;
  onDelete(sheet: SheetId): void;
  onDuplicate?(sheet: SheetId): void;
  onNotice?(message: string): void;
}

export interface SheetTabs {
  element: HTMLElement;
  /** Redraws the strip. Cheap: a workbook holds at most 10 tabs. */
  render(tabs: readonly SheetTab[], active: SheetId): void;
  /** Starts an inline rename of one tab. */
  beginRename(sheet: SheetId): void;
}

export function createSheetTabs(
  callbacks: SheetTabsCallbacks,
  rightSlot?: HTMLElement,
): SheetTabs {
  const strip = el('div', {
    className: 'tab-strip',
    testid: 'sheet-tabs',
    role: 'tablist',
    ariaLabel: 'Sheets',
  });

  const menuBtn = button('≡', () => toggleSheetMenu(), 'sheet-menu');
  menuBtn.className = 'tab-menu-btn';
  menuBtn.title = 'All sheets menu';

  const add = button('+', () => callbacks.onAdd(), 'add-sheet');
  add.className = 'tab-add';
  add.setAttribute('aria-label', 'Add sheet');
  add.title = 'Add sheet';

  const sheetDropdown = el('div', {
    className: 'tab-menu-dropdown',
    attrs: { hidden: 'hidden' },
  });

  const children: HTMLElement[] = [menuBtn, add, strip, sheetDropdown];
  if (rightSlot) children.push(rightSlot);

  const element = el('div', { className: 'tab-bar' }, children);

  let tabs: readonly SheetTab[] = [];
  let active: SheetId | null = null;
  let renaming: SheetId | null = null;

  const hiddenSheets = new Set<SheetId>();
  const lockedSheets = new Set<SheetId>();
  const tabColors = new Map<SheetId, string>();

  function toggleSheetMenu(): void {
    const isHidden = sheetDropdown.hidden;
    sheetDropdown.hidden = !isHidden;
    if (!sheetDropdown.hidden) {
      renderSheetMenu();
    }
  }

  function renderSheetMenu(): void {
    sheetDropdown.replaceChildren();
    tabs.forEach((tab) => {
      const isCurrent = tab.id === active;
      const isHidden = hiddenSheets.has(tab.id);
      const isLocked = lockedSheets.has(tab.id);
      const labelText = (isCurrent ? '✓ ' : '   ') + tab.name + (isHidden ? ' (Hidden)' : '') + (isLocked ? ' 🔒' : '');
      const item = el('div', {
        className: isCurrent ? 'tab-menu-item is-active' : 'tab-menu-item',
        text: labelText,
      });
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        sheetDropdown.hidden = true;
        if (isHidden) {
          hiddenSheets.delete(tab.id);
          callbacks.onNotice?.(`Unhid sheet ${tab.name}`);
        }
        callbacks.onActivate(tab.id);
        draw();
      });
      sheetDropdown.appendChild(item);
    });
  }

  let activeTabContextMenu: HTMLElement | null = null;

  function closeTabContextMenu(): void {
    if (activeTabContextMenu !== null) {
      activeTabContextMenu.remove();
      activeTabContextMenu = null;
    }
  }

  document.addEventListener('click', (ev) => {
    if (!sheetDropdown.hidden && !element.contains(ev.target as Node)) {
      sheetDropdown.hidden = true;
    }
    if (activeTabContextMenu !== null && !activeTabContextMenu.contains(ev.target as Node)) {
      closeTabContextMenu();
    }
  });

  function startRename(sheet: SheetId): void {
    renaming = sheet;
    draw();
  }

  function openTabColorSubmenu(tab: SheetTab, tabNode: HTMLElement): void {
    closeTabContextMenu();
    const menu = el('div', { className: 'sheet-tab-context-menu sheet-tab-color-menu' });
    const colors = ['#0d9488', '#2563eb', '#dc2626', '#d97706', '#7c3aed', '#db2777', '#475569'];

    const title = el('div', { className: 'sheet-tab-menu-title', text: 'Select Tab Color' });
    menu.appendChild(title);

    const palette = el('div', { className: 'sheet-tab-color-palette' });
    colors.forEach((color) => {
      const swatch = el('div', { className: 'sheet-tab-color-swatch' });
      swatch.style.backgroundColor = color;
      swatch.addEventListener('click', (e) => {
        e.stopPropagation();
        tabColors.set(tab.id, color);
        closeTabContextMenu();
        callbacks.onNotice?.(`Sheet ${tab.name} tab color updated.`);
        draw();
      });
      palette.appendChild(swatch);
    });
    menu.appendChild(palette);

    const reset = el('div', { className: 'sheet-tab-menu-item', text: 'Reset color' });
    reset.addEventListener('click', (e) => {
      e.stopPropagation();
      tabColors.delete(tab.id);
      closeTabContextMenu();
      callbacks.onNotice?.(`Sheet ${tab.name} tab color reset.`);
      draw();
    });
    menu.appendChild(reset);

    const rect = tabNode.getBoundingClientRect();
    const barRect = element.getBoundingClientRect();
    menu.style.left = `${Math.max(8, rect.left - barRect.left)}px`;
    element.appendChild(menu);
    activeTabContextMenu = menu;
  }

  function openTabContextMenu(tab: SheetTab, tabNode: HTMLElement): void {
    closeTabContextMenu();
    const menu = el('div', { className: 'sheet-tab-context-menu' });
    const isLocked = lockedSheets.has(tab.id);

    const items = [
      { label: 'Delete', testid: `delete-${tab.id}`, action: () => callbacks.onDelete(tab.id) },
      { label: 'Rename', action: () => startRename(tab.id) },
      {
        label: 'Change color...',
        action: () => {
          openTabColorSubmenu(tab, tabNode);
        },
      },
      {
        label: 'Hide',
        action: () => {
          hiddenSheets.add(tab.id);
          callbacks.onNotice?.(`Sheet ${tab.name} hidden. Select from ≡ menu to unhide.`);
          if (active === tab.id) {
            const nextVisible = tabs.find((t) => !hiddenSheets.has(t.id));
            if (nextVisible) callbacks.onActivate(nextVisible.id);
          }
          draw();
        },
      },
      {
        label: 'Duplicate',
        action: () => {
          if (callbacks.onDuplicate) callbacks.onDuplicate(tab.id);
          else callbacks.onAdd();
          callbacks.onNotice?.(`Duplicated sheet ${tab.name}.`);
        },
      },
      {
        label: isLocked ? 'Unlock sheet' : 'Lock sheet',
        action: () => {
          if (isLocked) {
            lockedSheets.delete(tab.id);
            callbacks.onNotice?.(`Sheet ${tab.name} unlocked.`);
          } else {
            lockedSheets.add(tab.id);
            callbacks.onNotice?.(`Sheet ${tab.name} locked (read-only).`);
          }
          draw();
        },
      },
    ];

    items.forEach((item) => {
      const row = el('div', {
        className: 'sheet-tab-menu-item',
        text: item.label,
        ...(item.testid ? { testid: item.testid } : {}),
      });
      row.addEventListener('click', (e) => {
        e.stopPropagation();
        closeTabContextMenu();
        item.action();
      });
      menu.appendChild(row);
    });

    const rect = tabNode.getBoundingClientRect();
    const barRect = element.getBoundingClientRect();
    menu.style.left = `${Math.max(8, rect.left - barRect.left)}px`;
    element.appendChild(menu);
    activeTabContextMenu = menu;
  }

  function draw(): void {
    closeTabContextMenu();
    strip.textContent = '';
    for (const tab of tabs) {
      if (hiddenSheets.has(tab.id)) continue;
      const selected = tab.id === active;
      if (renaming === tab.id) {
        const input = el('input', {
          className: 'tab-rename',
          testid: `rename-${tab.id}`,
          ariaLabel: `Rename ${tab.name}`,
          attrs: { type: 'text', value: tab.name },
        });
        input.value = tab.name;
        input.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            const name = input.value.trim();
            renaming = null;
            if (name.length > 0 && name !== tab.name) callbacks.onRename(tab.id, name);
            else draw();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            renaming = null;
            draw();
          }
        });
        input.addEventListener('blur', () => {
          if (renaming !== tab.id) return;
          renaming = null;
          draw();
        });
        strip.appendChild(input);
        input.focus();
        input.select();
        continue;
      }

      const isLocked = lockedSheets.has(tab.id);
      const tabColor = tabColors.get(tab.id);

      const label = el('span', { className: 'tab-label', text: tab.name + (isLocked ? ' 🔒' : '') });
      const chevron = button('▾', () => {
        openTabContextMenu(tab, node);
      }, `tab-chevron-${tab.id}`);
      chevron.className = 'tab-chevron';
      chevron.addEventListener('click', (e) => e.stopPropagation());

      const deleteAction = el('span', {
        className: 'tab-delete-action',
        testid: `delete-${tab.id}`,
        attrs: { style: 'display:none;' },
      });
      deleteAction.addEventListener('click', (e) => {
        e.stopPropagation();
        callbacks.onDelete(tab.id);
      });

      const node = el(
        'div',
        {
          className: selected ? 'tab tab-active' : 'tab',
          testid: `tab-${tab.id}`,
          role: 'tab',
          attrs: {
            'aria-selected': selected ? 'true' : 'false',
            tabindex: selected ? '0' : '-1',
          },
        },
        [label, chevron, deleteAction],
      );

      if (tabColor) {
        node.style.borderBottom = `3px solid ${tabColor}`;
      }

      node.addEventListener('mousedown', (event: MouseEvent) => {
        // Prevents tab click from stealing focus away from active formula editor session
        event.preventDefault();
      });
      node.addEventListener('click', () => {
        callbacks.onActivate(tab.id);
      });
      node.addEventListener('contextmenu', (event: MouseEvent) => {
        event.preventDefault();
        openTabContextMenu(tab, node);
      });
      node.addEventListener('dblclick', () => {
        startRename(tab.id);
      });
      node.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'F2') {
          event.preventDefault();
          startRename(tab.id);
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          callbacks.onActivate(tab.id);
        }
      });
      strip.appendChild(node);
    }
  }

  return {
    element,

    render(next: readonly SheetTab[], activeSheet: SheetId): void {
      tabs = [...next].sort((a, b) => a.index - b.index);
      active = activeSheet;
      if (renaming !== null && !tabs.some((t) => t.id === renaming)) renaming = null;
      draw();
    },

    beginRename(sheet: SheetId): void {
      renaming = sheet;
      draw();
    },
  };
}
